import { DeleteOutlined } from '@ant-design/icons'
import { ShareSelectInput } from '@components'
import {
  EBlogNewsTabs,
  EEventPostType,
  EEventPostTypeLabel,
  PATH_EVENT_POST,
} from '@configs'
import { zodResolver } from '@hookform/resolvers/zod'
import { RootState, useAppDispatch, selectEventPostsLoading } from '@redux'
import { DatePicker, message, Tabs } from 'antd'
import { t } from 'i18next'
import moment from 'moment'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ApiClient } from 'src/api'
import { workshopEventApi } from 'src/api/workshopEvent'
import { Button, Input, SwitchButton, Text } from 'src/common'
import { CustomDragger } from 'src/common/upload/CustomDragger'
import AddNewBlogModal from 'src/components/blog/AddNewBlogModal'
import DeleteBlogModal from 'src/components/blog/DeleteBlogModal'
import PreviewPhotoModal from 'src/components/chatChannel/PreviewPhotoModal'
import SelectMedia from 'src/components/gallery/SelectMedia'
import SelectMediaModal from 'src/components/gallery/SelectMediaModal'
import {
  ICreateEventPostValue,
  IResponseGetEventPostDetail,
} from 'src/interfaces/event-posts'
import { EMediaCategory, EMediaType, IMediaItem } from 'src/interfaces/gallery'
import {
  createEventPostAction,
  deleteEventPostAction,
  getEventPostDetailAction,
  updateEventPostAction,
} from 'src/redux/actions/event-posts'
import { getUserChat } from 'src/redux/actions/chat'
import { customSlugify } from 'src/utils/customSlugify'
import styled from 'styled-components'
import * as z from 'zod'
import { EventContentTab, EventMetaTab } from './content'

const eventTypeOptions = [
  {
    label: EEventPostTypeLabel.EVENT_POST_NEWS,
    value: EEventPostType.EVENT_POST_NEWS,
  },
  {
    label: EEventPostTypeLabel.EVENT_POST_RECAP,
    value: EEventPostType.EVENT_POST_RECAP,
  },
]

const SingleEvent = () => {
  const { eventId } = useParams<{ eventId?: string }>()
  const [searchParams] = useSearchParams()
  const presetType = searchParams.get('type')

  let selectedEventPost = useSelector(
    (state: RootState) => state.eventPosts.selectedEventPost
  )
  selectedEventPost = Boolean(eventId)
    ? selectedEventPost
    : ({} as IResponseGetEventPostDetail)

  // Extract the actual data from the response wrapper
  const eventData = selectedEventPost?.data

  const [activeTab, setActiveTab] = useState<string>(EBlogNewsTabs.CONTENT)
  const [dataSubmit, setDataSubmit] = useState<any>({} as any)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [previewImage] = useState('')
  const [detailImg, setDetailImg] = useState('')
  const [slug, setSlug] = useState(eventData?.urlSlug || '')
  const [selectedBlogType, setSelectedBlogType] = useState<string>(
    eventData?.type ||
      (presetType === 'event'
        ? EEventPostType.EVENT_POST_NEWS
        : EEventPostType.EVENT_POST_NEWS)
  )
  const [contentLoaded, setContentLoaded] = useState(!eventId) // true for new, false for edit until loaded

  const [showModalSelectMedia, setShowModalSelectMedia] = useState(false)
  const [selectedField, setSelectedField] = useState<
    'desktop' | 'mobile' | 'gallery' | null
  >(null)
  const onChangeDesktopRef = useRef<any>(null)

  const [reset] = useState(false)
  const [isModalLoading, setIsModalLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [imageSrc, setImageSrc] = useState(eventData?.thumbnail?.url ?? '')
  const [eventImages, setEventImages] = useState<any[]>([])
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Event list from Weeztix for Event ID dropdown
  const [listEventFromWT, setListEventFromWT] = useState<any[]>([])
  const [isGettingListEventFromWT, setIsGettingListEventFromWT] =
    useState(false)

  // Get loading states for create/update actions
  const isCreatingEventPost = useSelector((state: RootState) =>
    selectEventPostsLoading(state, 'createEventPostAction')
  )
  const isUpdatingEventPost = useSelector((state: RootState) =>
    selectEventPostsLoading(state, 'updateEventPostAction')
  )

  // Dynamic schema for event types
  const blogSchema = useMemo(() => {
    return z.object({
      title: z
        .string()
        .trim()
        .nonempty({
          message: t('error:event_title_required') as string,
        })
        .max(150, {
          message: t('error:event_title_max_length') as string,
        }),
      content:
        selectedBlogType === EEventPostType.EVENT_POST_RECAP
          ? z.string().optional() // Content is optional for recap events
          : z
              .string()
              .trim()
              .nonempty({
                message: t('error:event_content_required') as string,
              }),
      urlSlug: z
        .string()
        .trim()
        .nonempty({
          message: t('error:slug_required') as string,
        })
        .max(255, {
          message: t('error:slug_length_error') as string,
        }),
      metaTitle: z
        .string()
        .trim()
        .nonempty({
          message: t('error:field_required') as string,
        })
        .max(100, {
          message: t('error:metaTitle_length_error') as string,
        }),
      metaDescription: z
        .string()
        .trim()
        .nonempty({
          message: t('error:field_required') as string,
        })
        .max(255, {
          message: t('error:metaDescription_length_error') as string,
        }),
      canonical: z
        .string()
        .trim()
        .nonempty({
          message: t('error:field_required') as string,
        })
        .max(255, {
          message: t('error:canonical_length_error') as string,
        }),
      schemaMarkup: z
        .string()
        .trim()
        .nonempty({
          message: t('error:field_required') as string,
        })
        .max(255, {
          message: t('error:metaDescription_length_error') as string,
        })
        .default('schema mark up'),
      metaImage: z
        .string()
        .trim()
        .nonempty({
          message: t('error:field_required') as string,
        }),
      keywords: z
        .string({
          invalid_type_error: t('error:field_required') as string,
        })
        .trim()
        .nonempty({
          message: t('error:field_required') as string,
        })
        .max(255, {
          message: t('error:metaKeyword_length_error') as string,
        }),
      // Event ID is required for EVENT_POST_RECAP, optional for EVENT_POST_NEWS
      eventId:
        selectedBlogType === EEventPostType.EVENT_POST_RECAP
          ? z
              .string({ required_error: t('error:field_required') as string })
              .trim()
              .nonempty({
                message: t('error:field_required') as string,
              })
          : z.string().optional(),
      eventDate: z.string({
        required_error: t('error:event_date_invalid') as string,
        invalid_type_error: t('error:event_date_invalid') as string,
      }),
      type: z
        .string()
        .trim()
        .nonempty({
          message: t('error:event_type_required') as string,
        }),
      published: z.boolean(),
      thumbnailId: z.number({
        required_error: t('error:event_thumbnail_required') as string,
      }),
      isDiscover: z.boolean(),
      location: z
        .string()
        .trim()
        .nonempty({
          message: t('error:event_location_required') as string,
        }),
      mediaIds: z.array(z.number()).optional(),
    })
  }, [selectedBlogType])

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    clearErrors,
    setError,
    getValues,
    trigger,
  } = useForm<ICreateEventPostValue>({
    defaultValues: {
      title: eventData?.title || '',
      content: eventData?.content || '',
      urlSlug: eventData?.slug || eventData?.metaData?.urlSlug || '',
      metaTitle: eventData?.metaData?.metaTitle || '',
      metaDescription: eventData?.metaData?.metaDescription || '',
      canonical: eventData?.metaData?.canonical || '',
      schemaMarkup: 'schema mark up',
      metaImage: eventData?.metaData?.metaImage || '',
      keywords: eventData?.metaData?.keywords || '',
      eventId: eventData?.eventId || undefined,
      eventDate: eventData?.eventDate
        ? typeof eventData.eventDate === 'string'
          ? eventData.eventDate
          : moment(eventData.eventDate).toISOString()
        : undefined,
      type: eventData?.type || EEventPostType.EVENT_POST_NEWS,
      published: eventData?.published || false,
      thumbnailId: eventData?.thumbnailId,
      isDiscover: eventData?.isDiscover || false,
      location: eventData?.location || '',
      mediaIds: eventData?.medias?.map((m) => m.id) || [],
    },
    resolver: zodResolver(blogSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  // Check if have error in tabs
  const isContentTabError = useMemo(() => {
    return (
      !!errors?.content || !!errors.content?.message || !!errors?.content?.type
    )
  }, [errors?.content])

  const isMetaTabError = useMemo(() => {
    return (
      !!errors?.urlSlug ||
      !!errors?.metaTitle ||
      !!errors?.metaDescription ||
      !!errors?.canonical ||
      !!errors?.keywords ||
      !!errors?.metaImage
    )
  }, [
    errors?.urlSlug,
    errors?.metaTitle,
    errors?.metaDescription,
    errors?.canonical,
    errors?.keywords,
    errors?.metaImage,
  ])

  const onClickSelectGalleryMedia = () => {
    setSelectedField('gallery')
    setShowModalSelectMedia(true)
  }

  // Fetch events from Workshop Event for Event dropdown
  const getListEventFromWT = async () => {
    try {
      setIsGettingListEventFromWT(true)
      const res = await workshopEventApi.getListWorkshopEvent({
        page: 1,
        limit: 1000,
      })
      if (res.success) {
        const data = res.data?.items?.map((item) => ({
          value: item.guid,
          label: item.name,
          location: item.location
            ? `${item.location.name}, ${item.location.address}`
            : '',
          start: item.start ? moment(item.start).toISOString() : '',
          eventDate: item.start ? moment(item.start).toISOString() : undefined,
        }))
        setListEventFromWT(data || [])
      }
    } catch (error: any) {
      message.error({
        content: error?.message ?? t('error:Can not get list events select'),
      })
    } finally {
      setIsGettingListEventFromWT(false)
    }
  }

  const tabItems = [
    {
      label: (
        <Text
          className="blog-content-label"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: `${isContentTabError ? 'red' : '#009BB7'}`,
          }}
        >
          <div className="label-wrapper-content">{EBlogNewsTabs.CONTENT}</div>
        </Text>
      ),
      key: EBlogNewsTabs.CONTENT,
      children: contentLoaded ? (
        <EventContentTab
          activeTab={activeTab}
          control={control}
          setValue={setValue}
          clearErrors={clearErrors}
          setError={setError}
          errors={errors}
          getValues={getValues}
          contentName="content"
          eventId={eventId}
          eventType={selectedBlogType}
          eventImages={eventImages}
          setEventImages={setEventImages}
          onClickSelectThumbnail={onClickSelectGalleryMedia}
          isModalLoading={isModalLoading}
        />
      ) : (
        <div className="flex items-center justify-center p-8">
          <div className="text-gray-500">Loading content...</div>
        </div>
      ),
    },
    {
      label: (
        <Text
          className="blog-meta-label"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: `${isMetaTabError ? 'red' : '#009BB7'}`,
          }}
        >
          <div className="label-wrapper-content">{EBlogNewsTabs.META}</div>
        </Text>
      ),
      key: EBlogNewsTabs.META,
      children: (
        <EventMetaTab
          activeTab={activeTab}
          control={control}
          getValues={getValues}
          setValue={setValue}
          slug={customSlugify(slug)}
          isShowSelectMedia
          category={EMediaCategory.BLOG}
        />
      ),
    },
  ]

  const onClose = () => {
    setPreviewOpen(false)
  }
  const onCloseConfirmModal = () => {
    setOpenConfirmModal(false)
  }
  const onCloseDeleteModal = () => {
    setOpenDeleteModal(false)
  }

  const handleBlogTypeChange = (value: string) => {
    setSelectedBlogType(value)
    setValue('type', value)
    // Event posts don't have categories or tags
  }

  const onSubmit = async (data: ICreateEventPostValue) => {
    try {
      // Handle image uploads for EVENT_POST_RECAP
      if (
        data.type === EEventPostType.EVENT_POST_RECAP &&
        eventImages?.length
      ) {
        const imageObj = eventImages.reduce(
          (acc, currentItem) => {
            currentItem.id !== undefined
              ? acc.existedImages.push(currentItem)
              : acc.newImages.push(currentItem)
            return acc
          },
          { existedImages: [], newImages: [] }
        )

        const payload = {
          ...data,
          location: data.location || '',
          mediaIds: imageObj.existedImages.map((image: any) => image.id),
        }

        if (imageObj.newImages.length) {
          setIsUploading(true)
          try {
            const uploadArray = imageObj.newImages.map((image: any) => {
              const formData = new FormData()
              formData.append('file', image.file)
              return ApiClient.post('/media/resize-resolution', formData)
            })

            const responses = await Promise.all(uploadArray)
            payload.mediaIds = [
              ...responses.map((res) => res.data?.id),
              ...imageObj.existedImages.map((image: any) => image.id),
            ]
          } finally {
            setIsUploading(false)
          }
        }

        setDataSubmit(payload)
      } else {
        const eventPayload = {
          ...data,
          location: data.location || '',
          mediaIds:
            data.type === EEventPostType.EVENT_POST_RECAP ? [] : undefined,
        }
        setDataSubmit(eventPayload)
      }

      setOpenConfirmModal(true)
    } catch (error: any) {
      setIsUploading(false)
      message.error(error?.message || 'Failed to process images')
    }
  }

  const handleClickSave = () => {
    handleSubmit(onSubmit, (errors) => {
      // Form validation failed - errors will be shown in UI
    })()

    if (customSlugify(slug.trim()).length > 255) {
      setError('urlSlug', {
        type: 'maxLength',
        message: t('error:slug_length_error') as string,
      })
    }
  }

  const handleConfirmSubmit = async () => {
    if (eventId) {
      try {
        const response = await dispatch(
          updateEventPostAction({
            eventId: eventId,
            params: dataSubmit,
          })
        ).unwrap()

        if (response?.success) {
          message.success(response?.message || 'Event updated successfully')
          navigate(PATH_EVENT_POST)
        }
      } catch (error: any) {
        message.error(error?.message)
      }
    } else {
      try {
        const response = await dispatch(
          createEventPostAction(dataSubmit)
        ).unwrap()
        if (response?.success) {
          message.success(response?.message || 'Event created successfully')
          navigate(PATH_EVENT_POST)
        }
      } catch (error: any) {
        message.error(error?.message)
      }
    }
  }

  const handleConfirmDelete = async () => {
    try {
      const response = await dispatch(
        deleteEventPostAction(eventId as string)
      ).unwrap()

      if (response?.success) {
        message.success(response?.message || 'Event deleted successfully')
        navigate(PATH_EVENT_POST)
      }
    } catch (error: any) {
      message.error(error?.message)
    }
  }

  const onCloseModalSelectMedia = () => {
    setShowModalSelectMedia(false)
  }

  const onSelectMedia = (data: IMediaItem) => {
    if (selectedField === 'desktop') {
      setImageSrc(data?.baseUrl ?? '')
      setValue('thumbnailId', data?.id ?? 0)
      onChangeDesktopRef.current(data?.id ?? 0)
    } else if (selectedField === 'gallery') {
      const newImage = {
        id: data.id,
        src: data.baseUrl,
        name: data.name || `Image ${data.id}`,
      }

      const imageExists = eventImages.some((img) => img.id === data.id)

      if (!imageExists && eventImages.length < 20) {
        setEventImages((prev) => [...prev, newImage])
        const updatedMediaIds = [
          ...eventImages.map((img) => img.id).filter(Boolean),
          data.id,
        ]
        setValue('mediaIds', updatedMediaIds)
      } else if (imageExists) {
        message.warning('This image is already added to the gallery')
      } else if (eventImages.length >= 20) {
        message.error('You can only upload 20 images maximum')
      }
    }

    setShowModalSelectMedia(false)
  }

  useEffect(() => {
    const getDataRightSide = async () => {
      try {
        await dispatch(getUserChat({ page: '1', limit: '1000', search: '' }))
      } catch (error: any) {}
    }
    getDataRightSide()
    // Fetch events from Weeztix for Event ID dropdown
    getListEventFromWT()
  }, [dispatch])

  useEffect(() => {
    if (eventId) {
      const getEventDetail = async () => {
        try {
          const response = await dispatch(
            getEventPostDetailAction(eventId)
          ).unwrap()
          if (response?.success) {
            const eventDetail = response.data as any

            // Set form values
            setValue('title', eventDetail.title || '')
            setValue('content', eventDetail.content || '')
            setValue(
              'urlSlug',
              eventDetail.slug || eventDetail.metaData?.urlSlug || ''
            )
            setSlug(eventDetail.slug || eventDetail.metaData?.urlSlug || '')
            setValue('metaTitle', eventDetail.metaData?.metaTitle || '')
            setValue(
              'metaDescription',
              eventDetail.metaData?.metaDescription || ''
            )
            setValue('canonical', eventDetail.metaData?.canonical || '')
            setValue('metaImage', eventDetail.metaData?.metaImage || '')
            setValue('keywords', eventDetail.metaData?.keywords || '')
            setValue('eventId', eventDetail.eventId || '')

            // Handle eventDate - ensure it's always an ISO string
            if (eventDetail.eventDate) {
              const dateValue =
                typeof eventDetail.eventDate === 'string'
                  ? eventDetail.eventDate
                  : moment(eventDetail.eventDate).toISOString()
              setValue('eventDate', dateValue)
            }

            setValue('type', eventDetail.type)
            setValue('published', eventDetail.published)
            setValue('thumbnailId', eventDetail.thumbnailId)
            setValue('isDiscover', eventDetail.isDiscover ?? false)
            setValue('location', eventDetail.location || '')
            setValue(
              'mediaIds',
              eventDetail.medias?.map((m: any) => m.id) || []
            )

            // Set eventImages for recap type
            if (
              eventDetail.type === EEventPostType.EVENT_POST_RECAP &&
              eventDetail.medias
            ) {
              const existingImages = eventDetail.medias.map((media: any) => ({
                id: media.id,
                src: media.url, // MultipleUpload expects 'src' property
                name: media.name || `Image ${media.id}`,
              }))
              setEventImages(existingImages)
            }

            setSelectedBlogType(eventDetail.type)
            setDetailImg(eventDetail.thumbnail?.url || '')
            setImageSrc(eventDetail.thumbnail?.url || '')
            setSlug(eventDetail.urlSlug || '')
            setContentLoaded(true)
          }
        } catch (error: any) {
          console.error('Error loading event post detail:', error)
          setContentLoaded(true) // Still set to true to show empty form
        }
      }
      getEventDetail()
    }
  }, [eventId, dispatch, setValue])

  return (
    <SingleBlogStyled>
      {/* Top content */}
      <div className="blog-content-top intro-y mt-8 flex flex-col items-center sm:flex-row">
        <h2 className="blog-content-top mr-auto text-lg font-medium flex items-center justify-between">
          {Boolean(eventId) ? 'Edit Event Post' : 'Create New Event Post'}
        </h2>
        <div className="flex items-center justify-between gap-2 blog-content-top item-right-wrapper">
          {!!eventId ? (
            <Button
              type="default"
              size="small"
              icon={<DeleteOutlined />}
              className="flex items-center justify-start"
              onClick={() => {
                setOpenDeleteModal(true)
              }}
            >
              Delete
            </Button>
          ) : (
            <></>
          )}

          <Button
            type="primary"
            size="small"
            loading={isUploading || isCreatingEventPost || isUpdatingEventPost}
            disabled={isUploading || isCreatingEventPost || isUpdatingEventPost}
            onClick={() => {
              handleClickSave()
            }}
          >
            Save
          </Button>
        </div>
      </div>

      {/* Event container */}
      <div className="grid grid-cols-12 gap-5 mt-5 blog-content-main intro-y">
        <div className="col-span-12 blog-content-m-left intro-y lg:col-span-8">
          <Controller
            name="title"
            defaultValue=""
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <Input
                  placeholder="Event Title"
                  value={value}
                  onChange={(event) => {
                    setSlug(event?.target?.value?.trim() || '')
                    onChange(event?.target?.value)
                    setValue(
                      'urlSlug',
                      customSlugify(event?.target?.value?.trim())
                    )
                  }}
                  errors={error?.message}
                />
              )
            }}
          />

          {/* Content tabs */}
          <div className="mt-5 overflow-hidden intro-y box">
            <Tabs
              destroyInactiveTabPane={true}
              defaultActiveKey={EBlogNewsTabs.CONTENT}
              type="card"
              size={'small'}
              items={tabItems}
              activeKey={activeTab}
              onChange={(e) => {
                setActiveTab(e)
              }}
            />
          </div>
        </div>

        {/* Right sidebar */}
        <div className="col-span-12 rounded-lg blog-content-m-right lg:col-span-4">
          <div className="p-5 bg-white rounded-lg intro-y box">
            {/* Event ID Field - Dropdown with Auto-population */}
            <div className="mt-3 blog-event-id">
              <Controller
                name="eventId"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  return (
                    <ShareSelectInput
                      placeholder={t('evolveLanding:search_workshopEvent')}
                      errors={error?.message}
                      label="Event"
                      inlineLabelClassName="text-dark !font-medium"
                      required={
                        selectedBlogType === EEventPostType.EVENT_POST_RECAP
                      }
                      loading={isGettingListEventFromWT}
                      options={listEventFromWT as any}
                      showSearch
                      showArrow
                      value={!isGettingListEventFromWT ? value : undefined}
                      onSelect={(data, option) => {
                        onChange(data)
                        setValue('eventDate', option.start)
                        setValue('location', option.location)
                        trigger(['eventDate', 'location'])
                      }}
                    />
                  )
                }}
              />
            </div>

            <div className="mt-3 blog-post-date">
              <div className="mb-2">Event Date</div>
              <Controller
                name="eventDate"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  return (
                    <Fragment>
                      <DatePicker
                        className="w-full blog-time-select end-time-input"
                        placeholder="Select event date"
                        value={
                          value && moment(value).isValid()
                            ? moment(value)
                            : undefined
                        }
                        onChange={(date, dateString) => {
                          if (date && moment(date).isValid()) {
                            setValue('eventDate', date.toISOString())
                          } else {
                            setValue('eventDate', undefined as any)
                          }
                          if (dateString === '') {
                            setError('eventDate', {
                              type: 'required',
                              message: 'Event date is required',
                            })
                          } else {
                            clearErrors(['eventDate'])
                          }
                        }}
                        disabled={
                          selectedBlogType === EEventPostType.EVENT_POST_RECAP
                        }
                        status={error?.message ? 'error' : ''}
                        format={'DD-MM-YYYY'}
                        style={{
                          height: '38px',
                          borderRadius: '0.375rem',
                          outline: 'none',
                        }}
                      />
                      {error && error.message && (
                        <p className="mt-2 text-sm text-red-600">
                          {error.message}
                        </p>
                      )}
                    </Fragment>
                  )
                }}
              />
            </div>

            {/* Event Location */}
            <div className="mt-3 blog-event-location">
              <Controller
                name="location"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  return (
                    <Input
                      label="Event Location"
                      value={value || ''}
                      onChange={onChange}
                      errors={error?.message}
                      placeholder="Where did/will the event take place?"
                      alignment="col"
                      required
                      disabled={
                        selectedBlogType === EEventPostType.EVENT_POST_RECAP
                      }
                    />
                  )
                }}
              />
            </div>

            <div className="mt-3 blog-type">
              <Controller
                name="type"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  return (
                    <ShareSelectInput
                      data={eventTypeOptions}
                      label="Event Type"
                      onChange={(selectedValue) => {
                        onChange(selectedValue)
                        handleBlogTypeChange(selectedValue)
                      }}
                      value={value}
                      required
                      showArrow
                    />
                  )
                }}
              />
            </div>

            <div className="mt-3 blog-published">
              <div className="mb-2 blog-published-label">Published</div>
              <Controller
                name="published"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <SwitchButton
                      size="default"
                      onChange={onChange}
                      checked={value}
                    />
                  )
                }}
              />
            </div>

            <div className="mt-3 blog-published">
              <div className="mb-2 blog-show-author-label">Discover to app</div>
              <Controller
                name="isDiscover"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <SwitchButton
                      size="default"
                      onChange={onChange}
                      checked={value}
                    />
                  )
                }}
              />
            </div>

            {/* Thumbnail for both EVENT_NEWS and EVENT_RECAP */}
            {(selectedBlogType === EEventPostType.EVENT_POST_NEWS ||
              selectedBlogType === EEventPostType.EVENT_POST_RECAP) && (
              <div className="blog-published mt-3">
                <div className="wrapper-thumbnail-section mb-2">
                  <div className="blog-add-thumbnail-label">
                    Event Thumbnail{' '}
                    <span
                      className="required"
                      style={{ color: '#d42a1c', fontWeight: 'bold' }}
                    >
                      *
                    </span>
                  </div>
                </div>
                {previewImage || detailImg ? (
                  <Controller
                    name="thumbnailId"
                    control={control}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => {
                      onChangeDesktopRef.current = onChange
                      return (
                        <>
                          <CustomDragger
                            containerClassName="mt-[10px]"
                            onLoadEnd={(data) => {
                              setValue('thumbnailId', data?.id ?? 0)
                              onChange(data?.id ?? 0)
                              // setIsChange(true)
                            }}
                            name="mediaId"
                            id="mediaId"
                            required
                            errors={error?.message}
                            multiple={false}
                            initResource={imageSrc}
                            reset={reset}
                            allowFileTypes={[
                              'image/png',
                              'image/jpeg',
                              'image/jpg',
                            ]}
                            limitFileSize={5}
                            changeLoading={setIsModalLoading}
                            uploadType={EMediaType.IMAGE as any}
                            uploadCategory={EMediaCategory.BLOG}
                          />
                          <SelectMedia
                            onClickSelectMedia={() => {
                              setSelectedField((pre) => 'desktop')
                              setShowModalSelectMedia(true)
                            }}
                            isNotGrid={true}
                            isLoadingMedia={isModalLoading}
                          />
                        </>
                      )
                    }}
                  />
                ) : (
                  <Controller
                    name="thumbnailId"
                    control={control}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => {
                      onChangeDesktopRef.current = onChange
                      return (
                        <>
                          <CustomDragger
                            containerClassName="mt-[10px]"
                            onLoadEnd={(data) => {
                              setValue('thumbnailId', data?.id ?? 0)
                              onChange(data?.id ?? 0)
                              // setIsChange(true)
                            }}
                            name="mediaId"
                            id="mediaId"
                            required
                            errors={error?.message}
                            multiple={false}
                            initResource={imageSrc}
                            reset={reset}
                            allowFileTypes={[
                              'image/png',
                              'image/jpeg',
                              'image/jpg',
                            ]}
                            limitFileSize={5}
                            changeLoading={setIsModalLoading}
                            uploadType={EMediaType.IMAGE as any}
                            uploadCategory={EMediaCategory.BLOG}
                          />
                          <SelectMedia
                            onClickSelectMedia={() => {
                              setSelectedField((pre) => 'desktop')
                              setShowModalSelectMedia(true)
                            }}
                            isNotGrid={true}
                            isLoadingMedia={isModalLoading}
                          />
                        </>
                      )
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <PreviewPhotoModal
        open={previewOpen}
        onDelete={() => {}}
        onSave={() => {}}
        onClose={onClose}
        url={previewImage || detailImg}
      />
      <AddNewBlogModal
        open={openConfirmModal}
        content={
          'This event post will be saved and published on your setting time, make sure you have been checking all content.'
        }
        onSave={handleConfirmSubmit}
        onClose={onCloseConfirmModal}
      />
      <DeleteBlogModal
        open={openDeleteModal}
        content={'Are you sure to delete this event post?'}
        onSave={handleConfirmDelete}
        onClose={onCloseDeleteModal}
      />
      {showModalSelectMedia && (
        <SelectMediaModal
          isOpen={showModalSelectMedia}
          onClose={onCloseModalSelectMedia}
          mediaType={EMediaType.IMAGE}
          category={EMediaCategory.BLOG}
          onSelectMedia={onSelectMedia}
        />
      )}
    </SingleBlogStyled>
  )
}

export default SingleEvent

const SingleBlogStyled = styled('div')(() => {
  return {
    '.ant-tabs-nav': {
      marginBottom: 0,
      paddingLeft: '1.25rem',

      '& .ant-tabs-tab': {
        borderRadius: '0.75rem 0.75rem 0 0 !important',

        '&.ant-tabs-tab-active': {
          span: {
            color: '#009BB7',
            fontWeight: '500',
          },
        },
      },
    },

    '.ant-upload.ant-upload-select-picture-card': {
      width: '100%',
      height: 'auto',
    },
  }
})
